// src/app/modules/Reports/pages/Reports.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { GenerateReportModal } from "./components/GenerateReportModal";
import Moment from 'moment';
import './reports.scss';

import {
  FileText, Calendar, Plus, Filter, BarChart2,
  TrendingUp, Users, AlertTriangle, Shield, RefreshCw,
  Download, Eye,
} from 'lucide-react';

export function Reports() {
  const dispatch     = useDispatch();
  const navigate     = useNavigate();
  const isMountedRef = useRef(true);

  const { org, reports, loading } = useSelector(
    (state) => ({
      org:     state.auth.organization,
      reports: state.dashboard.reports,
      loading: state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [dateRange,          setDateRange]          = useState('this_month');
  const [reportType,         setReportType]         = useState('all');
  const [showGenerateModal,  setShowGenerateModal]  = useState(false);

  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);
  useEffect(() => { if (org?.organization_id) fetchReports(); }, [dateRange, reportType]);

  function fetchReports() {
    if (!isMountedRef.current) return;
    const params = { organization_id: org.organization_id };
    if (reportType !== 'all') params.report_type = reportType;
    dispatch(actions.fetchAllData('reports', org.organization_id, params));
  }

  function handlePreview(report) {
    navigate(`/reports/view/${report.analytics_id || report.report_id}`);
  }

  function handleDownload(report) {
    if (report.file_url) {
      window.open(report.file_url, '_blank');
    } else if (report.data) {
      dispatch(actions.addData('export_report_pdf', { report_id: report.report_id }))
        .then(res => { if (res?.file_url) window.open(res.file_url, '_blank'); })
        .catch(console.error);
    }
  }

  function getScoreColor(score) {
    if (score >= 80) return { bg: '#F0FDF4', color: '#16a34a' };
    if (score >= 60) return { bg: '#EFF6FF', color: '#2563EB' };
    if (score >= 40) return { bg: '#FFFBEB', color: '#D97706' };
    return { bg: '#FEF2F2', color: '#DC2626' };
  }

  function getTypeIcon(type) {
    const map = {
      monthly:  <BarChart2 size={18} />,
      weekly:   <TrendingUp size={18} />,
      security: <Shield size={18} />,
      incident: <AlertTriangle size={18} />,
      visitor:  <Users size={18} />,
    };
    return map[type] || <FileText size={18} />;
  }

  function getTypeColors(type) {
    const map = {
      monthly:  { bg: '#F0EEFF', color: '#5341C4' },
      weekly:   { bg: '#EFF6FF', color: '#2563EB' },
      security: { bg: '#F0FDF4', color: '#16a34a' },
      incident: { bg: '#FEF2F2', color: '#DC2626' },
      visitor:  { bg: '#FFFBEB', color: '#D97706' },
    };
    return map[type] || { bg: '#F8F8FC', color: '#9597A6' };
  }

  const filteredReports = reports || [];

  return (
    <>
      <div className="reports-page">

        {/* ── Header ── */}
        <div className="rp-header">
          <div className="rp-header__left">
            <h1>Security Reports</h1>
            <p>{org?.name} · Automated analytics and insights</p>
          </div>
          <div className="rp-header__actions">
            <button className="rp-btn rp-btn--secondary" onClick={fetchReports}>
              <RefreshCw size={13} />Refresh
            </button>
            {/* <button className="rp-btn rp-btn--primary" onClick={() => setShowGenerateModal(true)}>
              <Plus size={13} />Generate Report
            </button> */}
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="rp-card">
          <div className="rp-card__body" style={{ padding: '11px 16px' }}>
            <div className="rp-filters">
              <select className="rp-select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="this_year">This Year</option>
              </select>

              <select className="rp-select" value={reportType} onChange={(e) => setReportType(e.target.value)}>
                <option value="all">All Types</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="security">Security</option>
                <option value="incident">Incident</option>
                <option value="visitor">Visitor</option>
              </select>

              <button className="rp-apply-btn" onClick={() => { setDateRange('this_month'); setReportType('all'); }}>
                <Filter size={13} />Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="rp-card">
            <div className="rp-loading">
              <RefreshCw size={28} className="rp-spin" />
              <p>Loading reports…</p>
            </div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="rp-card">
            <div className="rp-empty">
              <FileText size={40} />
              <p>No reports available</p>
              <small>Generate your first report to get started</small>
              <button className="rp-btn rp-btn--primary" style={{ marginTop: 16 }} onClick={() => setShowGenerateModal(true)}>
                <Plus size={13} />Generate Report
              </button>
            </div>
          </div>
        ) : (
          <div className="rp-grid">
            {filteredReports.map((report) => {
              const score      = report.security_score?.overall_score || 0;
              const scoreStyle = getScoreColor(score);
              const typeStyle  = getTypeColors(report.report_type);

              return (
                <div key={report.analytics_id} className="rp-card rp-report-card">
                  {/* Card header row */}
                  <div className="rp-report-card__top">
                    <div className="rp-report-card__icon" style={{ background: typeStyle.bg, color: typeStyle.color }}>
                      {getTypeIcon(report.report_type)}
                    </div>
                    <div className="rp-report-card__badges">
                      <span className="rp-badge" style={{ background: typeStyle.bg, color: typeStyle.color }}>
                        {report.report_type}
                      </span>
                      <span className="rp-badge" style={{ background: scoreStyle.bg, color: scoreStyle.color }}>
                        Score: {score}
                      </span>
                    </div>
                  </div>

                  {/* Period */}
                  <div className="rp-report-card__period">
                    {Moment(report.period?.start_date).format('MMM DD')} – {Moment(report.period?.end_date).format('MMM DD, YYYY')}
                  </div>

                  {/* Quick stats */}
                  <div className="rp-report-card__stats">
                    <div className="rp-mini-stat">
                      <span className="rp-mini-stat__value">{report.visitor_analytics?.total_visitors || 0}</span>
                      <span className="rp-mini-stat__label">Visitors</span>
                    </div>
                    <div className="rp-mini-stat">
                      <span className="rp-mini-stat__value">{report.incident_analytics?.total_incidents || 0}</span>
                      <span className="rp-mini-stat__label">Incidents</span>
                    </div>
                    <div className="rp-mini-stat">
                      <span className="rp-mini-stat__value">{score}</span>
                      <span className="rp-mini-stat__label">Security Score</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="rp-report-card__footer">
                    <span className="rp-report-card__date">
                      <Calendar size={12} />
                      {Moment(report.generated_at).format('MMM DD, YYYY')}
                    </span>
                    <div className="rp-report-card__actions">
                      {/* <button className="rp-icon-btn" title="Preview" onClick={() => handlePreview(report)}>
                        <Eye size={14} />
                      </button> */}
                      <button className="rp-icon-btn" title="Download" onClick={() => handleDownload(report)}>
                        <Download size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      <GenerateReportModal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
        organizationId={org?.organization_id}
        onSuccess={fetchReports}
      />
    </>
  );
}